import { Activity } from '@shared/models/design/activities/activity.model';
import { ActivityPropertiesBase } from '@shared/models/design/activities/activity-properties-base.model';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import {
  resolveResidentialProgramType,
  resolveSchoolProgramType,
} from '@shared/models/design/activities/properties';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import { getActivityMixFromDesign } from '@shared/helpers/design/activity-mix.helpers';
import {
  ActivitySchedule,
  getActivitySchedules,
} from './space-activity.helpers';
import { isOneOf } from '../array.helpers';
import { sum } from '../math.helpers';
import { InsolationSurface } from './weather.models';

/** Boverket program subtype carried from design activities */
export type EnergyZoneActivityProgramType =
  | ReturnType<typeof resolveResidentialProgramType>
  | ReturnType<typeof resolveSchoolProgramType>;

export type EnergyZoneActivity = {
  type: ActivityType;
  area: number;
  schedules: ActivitySchedule[];
  properties: ActivityPropertiesBase;
  windows?: InsolationSurface[];
  /** Residential or school program subtype from design activity properties */
  programType?: EnergyZoneActivityProgramType;
};

const toNumericActivityProperties = (
  properties: Record<string, unknown> | undefined,
): ActivityPropertiesBase =>
  Object.fromEntries(
    Object.entries(properties ?? {}).filter(
      (entry): entry is [string, number] => typeof entry[1] === 'number',
    ),
  );

/**
 * Read persisted program_type from a design activity when relevant for energy.
 * @param activity Design activity
 * @returns Residential or school program subtype, if applicable
 */
const getProgramTypeFromActivity = (
  activity: Activity,
): EnergyZoneActivityProgramType | undefined => {
  switch (activity.type) {
    case ActivityType.Residential:
      return resolveResidentialProgramType(activity.properties.program_type);
    case ActivityType.School:
      return resolveSchoolProgramType(activity.properties.program_type);
    default:
      return undefined;
  }
};

const NON_HEATED_ACTIVITY_TYPES: ActivityType[] = [ActivityType.Garage];

/**
 * Build energy activities from building design, including areas and schedules.
 */
export const getEnergyActivitiesFromDesign = (
  design: BuildingDesign,
): EnergyZoneActivity[] => {
  const activityAreas = getActivityMixFromDesign(design);

  return design.activities
    .map((activity): EnergyZoneActivity | undefined => {
      const area = activityAreas[activity.type] ?? 0;
      if (area <= 0) return;

      // Use default schedules for the activity type if none are provided.
      return {
        type: activity.type,
        area,
        schedules: getActivitySchedules({ type: activity.type }),
        properties: toNumericActivityProperties(activity.properties),
        programType: getProgramTypeFromActivity(activity),
      };
    })
    .filter(
      (activity): activity is EnergyZoneActivity => activity !== undefined,
    );
};

/**
 * Get how many m2 of the activities that are heated.
 */
export const getHeatedAreaFromEnergyActivities = (
  activities: EnergyZoneActivity[],
): number => {
  return sum(
    activities.filter(
      (activity) => !isOneOf(NON_HEATED_ACTIVITY_TYPES, activity.type),
    ),
    'area',
  );
};
